import { Injectable } from '@angular/core';
import { Router, NavigationExtras, ActivatedRoute } from '@angular/router';
import { ModuleNames, ProductNames } from '@hospitality-bot/admin/shared';
import { NavRouteOption } from 'libs/admin/shared/src/index';
import { BehaviorSubject } from 'rxjs';
import { Location } from '@angular/common';
import { reject } from 'lodash';

const defaultNavigateConfig: NavigateConfig = {
  additionalPath: '',
  queryParams: {},
  isRespectiveToProduct: true, // Route will first open with respect to product
  isRelative: false,
  openInNewWindow: false,
};

// Need to fix... Module Names cannot be read here (Circular dependency)
const routesConfig = {
  ADD_RESERVATION: 'manage-reservation',
};

export class RouteConfigPathService {
  readonly routesConfig = routesConfig;
  readonly reverseRouteConfig = Object.keys(routesConfig).reduce(
    (reversed, key) => {
      reversed[routesConfig[key]] = key;
      return reversed;
    },
    {}
  );
  /**
   * Convert module/product name to route (underScore to dash)
   * Also checks if module name is attached with route
   * @param name Module or product name
   * @returns route
   */
  getRouteFromName(name: ModuleNames | ProductNames) {
    const route = this.routesConfig[name];
    if (route) return route;

    return name.toLowerCase().split('_').join('-');
  }

  getNameFromRoute(route: string) {
    const name = this.reverseRouteConfig[route];
    if (name) return name;

    return name.toUpperCase().split('-').join('_');
  }
}

@Injectable({ providedIn: 'root' })
export class RoutesConfigService extends RouteConfigPathService {
  constructor(
    private readonly router: Router,
    private readonly location: Location,
    private readonly route: ActivatedRoute
  ) {
    super();
  }

  /**
   * If same subModule in multiple product then first subscribed route respective to sub module name will be attached
   */
  modulePathConfig: ModulePathConfig = {};
  /**
   * Contains record of hierarchical route
   */
  hierarchicalPathConfig: HierarchicalPathConfig = {};

  moduleOfSubModuleWithRespectToProduct: ModuleOfSubModuleWithRespectToProduct = {};

  private $activeRoute = new BehaviorSubject<ActiveRouteConfig>({
    product: {
      shortPath: '',
      fullPath: '',
    },
    module: {
      shortPath: '',
      fullPath: '',
    },
    submodule: {
      shortPath: '',
      fullPath: '',
    },
  });

  private $navRoutes = new BehaviorSubject<NavRouteOption[]>([]);

  /**
   * Reload to the same route
   */
  reload() {
    const currentPath = window.location.pathname + window.location.search;
    this.navigate({ skipLocationChange: true }).then(() => {
      this.router.navigateByUrl(currentPath);
    });
  }

  navigate(config: Partial<NavigateConfig> = {}) {
    const {
      subModuleName,
      productName,
      additionalPath,
      queryParams,
      isRespectiveToProduct,
      openInNewWindow,
      isRelative,
      forceNavigation,
      ...restNavigationOption
    }: NavigateConfig = {
      ...defaultNavigateConfig,
      subModuleName: this.subModuleName,
      productName: this.productName,
      ...config,
    };

    // if relative to current path (Re think as it will Activated Route)
    // if (isRelative) {
    //   this.router.navigate([additionalPath], {
    //     queryParams: queryParams,
    //     relativeTo: ActivateRoute,
    //   });

    //   return;
    // }

    let path = this.modulePathConfig[subModuleName];

    let moduleName = config.moduleName; // Directly from params
    // If is respective to product then find module
    if (isRespectiveToProduct && this.productName && !moduleName) {
      moduleName = this.moduleOfSubModuleWithRespectToProduct[productName]?.[
        subModuleName
      ];
    }

    /**
     * If module name then route will open respective to product
     */
    if (moduleName && isRespectiveToProduct) {
      const newPath = this.hierarchicalPathConfig[productName]?.[moduleName]?.[
        subModuleName
      ];

      if (newPath) {
        path = newPath;
      }
    }

    if (path) {
      if (openInNewWindow) {
        const queryParamsStr = this.getQueryParamValue(queryParams);
        const url = `${path}${
          additionalPath ? `/${additionalPath}` : ''
        }${queryParamsStr}`;

        window.open(url);
      } else {
        const fullPath = `${path}${additionalPath ? `/${additionalPath}` : ''}`;

        // Handle force navigation
        if (forceNavigation) {
          return new Promise((resolve, reject) => {
            this.router
              .navigateByUrl('/', { skipLocationChange: true })
              .then(() =>
                this.router.navigate([fullPath], {
                  queryParams,
                  ...restNavigationOption,
                })
              )
              .then(resolve)
              .catch(reject);
          });
        } else {
          return this.router.navigate([fullPath], {
            queryParams: queryParams,
            ...restNavigationOption,
          });
        }
      }
    } else {
      console.error(
        'CANNOT NAVIGATE: Error in finding th path',
        config,
        subModuleName
      );
    }
  }

  getQueryParamValue(queryParams: NavigateConfig['queryParams']) {
    let queryParamsStr = '';
    if (queryParams) {
      queryParamsStr = '?';
      const paramArr = [];
      for (let queryKey in queryParams) {
        paramArr.push(`${queryKey}=${queryParams[queryKey]}`);
      }
      queryParamsStr = queryParamsStr + paramArr.join('&');
    }
    return queryParamsStr;
  }

  goBack() {
    if (window.history.length > 1) {
      this.location.back();
    } else {
      // Fallback using router, not window or Location
      this.router.navigate(['..'], { relativeTo: this.route }).catch(() => {
        this.router.navigate(['/']); // final fallback
      });
    }
  }

  getQueryValue(key: string) {
    return this.router.routerState.snapshot.root.queryParams[key] ?? '';
  }

  initActiveRoute(config: ActiveRouteConfig) {
    this.$activeRoute.next({ ...config });

    const navRoutes = new Array<NavRouteOption>();
    for (let item in config) {
      const currentConfig: PathConfig = config[item];
      const navRoute: NavRouteOption = {
        label: `${currentConfig?.label}`,
        link: currentConfig.fullPath,
        isDisabled: item !== 'submodule',
      };

      navRoutes.push(navRoute);
    }

    this.$navRoutes.next(navRoutes);
  }

  initModulePathConfig(
    res: ModulePathConfig,
    hRes: HierarchicalPathConfig,
    psmRes: ModuleOfSubModuleWithRespectToProduct
  ) {
    this.modulePathConfig = res;
    this.hierarchicalPathConfig = hRes;
    this.moduleOfSubModuleWithRespectToProduct = psmRes;
  }

  get activeRouteConfigSubscription() {
    return this.$activeRoute;
  }

  get navRoutesChanges() {
    return this.$navRoutes;
  }

  get defaultNavRouteOptions() {
    return this.$navRoutes.value;
  }

  get activeRouteConfig() {
    return this.$activeRoute.value;
  }

  get productName() {
    return this.activeRouteConfig.product.name;
  }

  get moduleName() {
    return this.activeRouteConfig.module.name;
  }

  get subModuleName() {
    return this.activeRouteConfig.submodule.name;
  }
}

/**
 * Example of a PathConfig object:
 * @example
 * const pathConfig = {
 *   shortPath: 'create-wth-dashboard',
 *   fullPath: 'create-with/create-with-home/create-wth-dashboard',
 *   name: 'CREATE_WITH_DASHBOARD',
 *   label: 'Dashboard'
 * };
 */
export type PathConfig<T = ModuleNames> = {
  shortPath: string;
  fullPath: string;
  name?: T;
  label?: string;
};

export type ActiveRouteConfig = {
  product: PathConfig<ProductNames>;
  module: PathConfig;
  submodule: PathConfig;
};

export type ModulePathConfig = Partial<Record<ModuleNames, string>>;
export type HierarchicalPathConfig = Partial<
  Record<
    ProductNames,
    Partial<Record<ModuleNames, Partial<Record<ModuleNames, string>>>>
  >
>;

export type NavigateConfig = {
  subModuleName?: ModuleNames;
  moduleName?: ModuleNames;
  productName?: ProductNames;
  additionalPath: string;
  queryParams: any;
  isRespectiveToProduct: boolean;
  isRelative: boolean;
  openInNewWindow: boolean;
  forceNavigation?: boolean;
} & NavigationExtras;

export type ModuleOfSubModuleWithRespectToProduct = Partial<
  Record<ProductNames, Partial<Record<ModuleNames, ModuleNames>>>
>;
